import React from 'react';
import { ReactComponent as IconBudget } from '../assets/icons/budget.svg';

const FeatureList = ({ number, title, description }) => (
    <div className="relative flex mb-6 md:mb-8 group">
        {/* Number */}
        <div className="absolute -left-4 md:-left-8 top-1/2 -translate-y-1/2 flex items-center justify-center w-16 md:w-32 z-20">
            <span className="text-4xl md:text-7xl font-bold text-white group-hover:text-yellow-400
                transition-colors duration-300 transform group-hover:scale-110">
                {number}
            </span>
        </div>

        {/* Card content */}
        <div className="flex-1 bg-[#2A2E35] rounded-lg p-4 md:p-6 ml-4 md:ml-8 group-hover:bg-[#373d47]
            transform transition-all duration-300 ease-in-out
            group-hover:-translate-y-2 group-hover:shadow-2xl relative">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-orange-400/20
                rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm">
            </div>

            {/* Content */}
            <div className="relative z-10">
                <h3 className="text-lg md:text-xl font-bold text-yellow-400 mb-1 md:mb-2
                    transform transition-all duration-300 group-hover:translate-x-1">
                    {title}
                </h3>
                <p className="text-sm md:text-base text-gray-300 leading-relaxed
                    transform transition-all duration-300 group-hover:text-gray-100">
                    {description}
                </p>
            </div>

            {/* Border glow */}
            <div className="absolute -inset-[1px] bg-gradient-to-r from-yellow-400 to-orange-400
                rounded-lg opacity-0 group-hover:opacity-30 -z-10 transition-opacity duration-300">
            </div>
        </div>
    </div>
);

const CircularDiagram = () => (
    <div className="relative w-[280px] md:w-[400px] h-[280px] md:h-[400px]">
        {/* Center Circle */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
                    w-24 md:w-32 h-24 md:h-32 bg-yellow-400 rounded-full flex items-center justify-center">
            <span className="text-xl md:text-2xl font-bold text-black">Төсөв</span>
        </div>

        {/* Outer Circle */}
        <svg
            className="w-full h-full"
            viewBox="0 0 400 400"
        >
            <defs>
                <linearGradient id="circleGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={{stopColor: '#FCD34D'}}/>
                    <stop offset="100%" style={{stopColor: '#F59E0B'}}/>
                </linearGradient>
            </defs>

            <circle
                cx="200"
                cy="200"
                r="180"
                fill="none"
                stroke="url(#circleGradient)"
                strokeWidth="40"
                strokeDasharray="1120"
                transform="rotate(-90 200 200)"
            />

            <text>
                <textPath
                    href="#circlePath"
                    className="text-base md:text-lg font-bold"
                    startOffset="0%"
                >
                    1. ТӨЛӨВЛӨХ • 2. БОЛОВСРУУЛАХ • 3. БАТЛАХ • 4. ГҮЙЦЭТГЭХ • 5. ТАЙЛАГНАХ, АУДИТ
                </textPath>
            </text>
        </svg>
    </div>
);

const SystemFeatures = () => {
    const features = [
        {
            number: "1",
            title: "ТӨЛӨВЛӨХ",
            description: "Чиг үүрэг, зорилтот түвшин, хүрэх үр дүн, хөтөлбөр, арга хэмжээг төлөвлөнө. Зардлын тооцооллыг стандарт маягтын дагуу оруулах."
        },
        {
            number: "2",
            title: "БОЛОВСРУУЛАХ",
            description: "Яам түүний харьяа агентлаг, байгууллагуудын шатлалт хооронд төсвийг боловсруулах."
        },
        {
            number: "3",
            title: "БАТЛАХ",
            description: "УИХ-аар улсын хэмжийн төсвийг хэлэлцэн батална."
        },
        {
            number: "4",
            title: "ГҮЙЦЭТГЭХ",
            description: "Батлагдсан төсвийг хуваарийн санал боловсруулан батлуулж хуваарийн дагуу зарцуулж, гүйцэтгэлийн явцыг хянана."
        },
        {
            number: "5",
            title: "ХЯНАХ",
            description: "Төсвийн төлөвлөгөөг бодит гүйцэтгэлтэй харьцуулан гүйцэтгэлийн үр дүнг байнга хянах боломжтой."
        }
    ];

    return (
        <div className="min-h-screen min-w-screen h-full flex flex-col md:flex-row items-center px-4 md:px-24 py-8 md:py-16 overflow-auto">
            {/* Mobile Title */}
            <div className="md:hidden mb-8">
                <h2 className="text-3xl font-bold text-center">
                    ТӨСӨВ
                </h2>
            </div>

            <div className="relative flex flex-1">
                {/* Desktop Vertical Title - Hidden on Mobile */}
                <div className="hidden md:block absolute top-1/2 -translate-y-1/2 items-center justify-center">
                    <h2 className="text-4xl font-bold transform -rotate-90 whitespace-nowrap origin-center">
                        ТӨСӨВ
                    </h2>
                </div>

                {/* Content Container */}
                <div className="flex flex-col md:flex-row flex-1 md:ml-32 p-4 md:p-50">
                    {/* Feature List */}
                    <div className="w-full md:w-1/2 md:pr-8 mb-8 md:mb-0">
                        {features.map((feature, index) => (
                            <FeatureList key={index} {...feature} />
                        ))}
                    </div>

                    {/* Circular Icon */}
                    <div className="w-full md:w-1/2 flex items-center justify-center">
                        <IconBudget className="w-[280px] h-[280px] md:w-[400px] md:h-[400px]" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SystemFeatures;